import React, { useMemo, useState } from 'react';
import {
  Divider,
  InlineInputGroupContainer,
  NameSuggestionList,
  NameSuggestionsContainer,
} from './InlineGenerator.styles';
import InlineGeneratorInput from '../../InlineNameGeneratorArticle/InlineGeneratorInput/InlineGeneratorInput';
import LoadingDots from '../../LoadingDots/LoadingDots';
import { getGeneratedResults } from 'src/functions/api/product/generators/getGeneratedResults';
import { transitionTimeInMilliseconds } from '../../InlineNameGeneratorArticle/InlineNameGenerator/NameSuggestion.styles';
import FadeInAnimationWrapper from 'app_previous/elements/animation/fade-in/FadeInAnimationWrapper';
import Paragraph from 'app_previous/elements/typography/paragraph';
import { palette } from 'app_previous/styles/colors';
import ButtonTertiary from 'app_previous/elements/buttons/Button.tertiary';
import NameSuggestion from '../../InlineNameGeneratorArticle/InlineNameGenerator/NameSuggestion';
import EmptyResultMessage from '../../InlineNameGeneratorArticle/InlineNameGenerator/EmptyResultMessage';
import { Theme } from '../../InlineNameGeneratorArticle/InlineNameGenerator/InlineNameGenerator.styles';
import getButtonStyleByColorScheme from 'app_previous/styles/functions/getButtonStyleByColorScheme';

interface InlineGeneratorProps {
  placeholder: string;
  helperText: string;
  generatorMode: string;
  generatorSubject: string;
  onSuccess?: (names: string[]) => void;
  colors?: Theme;
}

interface GeneratedNames {
  status?: 'loading' | 'error' | 'success';
  result: string[];
}

const InlineGenerator = ({
  placeholder,
  helperText,
  generatorMode,
  generatorSubject,
  onSuccess = null,
  colors,
}: InlineGeneratorProps): JSX.Element => {
  const [generatedNames, setGeneratedNames] = useState<GeneratedNames>({ status: null, result: [] });
  const [inputError, setInputError] = useState<string>(null);
  const [description, setDescription] = useState<string>('');
  const [numberOfNamesToShow, setNumberOfNamesToShow] = useState<number>(5);

  const { status, result: names } = generatedNames;

  const isLoading = (): boolean => status === 'loading';
  const isSuccess = (): boolean => status === 'success' && names.length > 0;
  const isEmptyResult = (): boolean => status === 'success' && names.length === 0;

  const handleSubmit = () => {
    if (isLoading()) return;

    if (description?.length > 60) {
      setInputError('Must be less than 60 characters');

      return;
    } else if (description?.length <= 3) {
      setInputError('Please enter more about what you want to generate');

      return;
    }

    setNumberOfNamesToShow(5);
    setInputError(null);
    setGeneratedNames({
      status: 'loading',
      result: [],
    });

    getGeneratedResults(description, generatorMode, generatorSubject)
      .then((result) => {
        setGeneratedNames({ status: 'success', result });
        onSuccess && onSuccess(result);
      })
      .catch((error) => {
        console.error(error);

        setGeneratedNames({ status: 'error', result: [] });
        setInputError('Something went wrong. Please try again.');
      });
  };

  const handleLoadMoreClick = () => setNumberOfNamesToShow(10);

  const namesToShow = useMemo(
    () =>
      [...names].slice(0, numberOfNamesToShow).map((name, index) => ({
        name,
        animated: numberOfNamesToShow < 10 || (numberOfNamesToShow === 10 && index >= 5),
        delayInMilliseconds: (index % 5) * transitionTimeInMilliseconds,
      })),
    [names, numberOfNamesToShow]
  );

  const shouldShowLoadMoreButton = numberOfNamesToShow < 10 && names.length > 5;

  return (
    <InlineInputGroupContainer id='inline-generator'>
      <InlineGeneratorInput
        placeholder={placeholder}
        helperText={helperText}
        description={description}
        setDescription={setDescription}
        inputError={inputError}
        handleSubmit={handleSubmit}
        buttonColor={colors?.buttonBackground}
        helperTextColor={colors?.text}
        buttonBorderColor={colors?.buttonBorderColor}
      />
      <LoadingDots
        background={colors?.buttonBackground ? () => colors?.buttonBackground : 'accentBlue'}
        loading={isLoading()}
      />
      {isSuccess() && (
        <>
          <Divider />
          <NameSuggestionsContainer>
            <FadeInAnimationWrapper>
              <Paragraph
                text='Here are some name suggestions:'
                standardSize='1.125'
                align='center'
                weight={400}
                color={colors?.secondaryText ?? palette.darkGray}
              />
            </FadeInAnimationWrapper>
            <NameSuggestionList>
              {namesToShow.map((name, index) => (
                <NameSuggestion
                  key={index}
                  name={name.name}
                  animated={name.animated}
                  delayTimeInMilliseconds={name.delayInMilliseconds}
                  backgroundColor={colors?.suggestionBackground}
                  textColor={colors?.secondaryText}
                  generatorSubject={generatorSubject}
                />
              ))}
            </NameSuggestionList>
            {shouldShowLoadMoreButton && (
              <ButtonTertiary
                text='Load More'
                id='load-more-button'
                colorScheme={colors?.link ?? 'AccentBlue'}
                onClick={handleLoadMoreClick}
                icon={
                  <svg
                    width='14'
                    height='17'
                    viewBox='0 0 14 17'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M6.19148 16.4238C6.61336 16.8809 7.35164 16.8809 7.77351 16.4238L13.3985 10.7988C13.8555 10.377 13.8555 9.63867 13.3985 9.2168C12.9766 8.75977 12.2384 8.75977 11.8165 9.2168L8.12508 12.9082V2.11523C8.12508 1.51758 7.59773 0.990234 7.00008 0.990234C6.36726 0.990234 5.87508 1.51758 5.87508 2.11523V12.9082L2.14851 9.2168C1.72664 8.75977 0.988357 8.75977 0.566482 9.2168C0.10945 9.63867 0.10945 10.377 0.566482 10.7988L6.19148 16.4238Z'
                      fill={
                        colors?.link
                          ? getButtonStyleByColorScheme(colors.link, 'tertiary').textColor
                          : palette.accentBlue
                      }
                    />
                  </svg>
                }
              />
            )}
          </NameSuggestionsContainer>
        </>
      )}
      {isEmptyResult() && <EmptyResultMessage />}
    </InlineInputGroupContainer>
  );
};

export default InlineGenerator;
